import React, { lazy, Suspense } from 'react'
import { getNameRelatedData } from '@fs/zion-person-service'
import { Trans, useTranslation } from 'react-i18next'
import { Paragraph, Ellipsis, useOverlay, Button } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { css } from '@linaria/core'
import type { BasePerson } from '@fs/zion-tree-types'
import InfoCard from '../info-card/PersonInfoCard'

const LifeHistoryDialog = lazy(() => import('./LifeHistoryDialog'))

const lifeOfTextCss = css`
  & > p {
    padding-bottom: 0;
  }
`

const wrapCss = css`
  white-space: pre-wrap;
`

type AboutPageLifeHistoryCardProps = AncestorsLifeHistoryCardProps & {
  /** Boolean to determine which info text to show, the text saying it is computer generated, or the text saying that it was user generated. */
  isUserGenerated?: boolean
}

export function AboutPageLifeHistoryCard({
  person,
  text,
  isUserGenerated,
  ...infoCardProps
}: AboutPageLifeHistoryCardProps): JSX.Element {
  const [t] = useTranslation()
  const { shortName } = getNameRelatedData(person)
  const overlay = useOverlay()
  const heading = t('info-cards.life-of.header', { shortName })
  return (
    <>
      <InfoCard heading={heading} {...infoCardProps}>
        <LifeHistoryText text={text} />
        <Button data-testid="more-button" onClick={overlay.handleClick}>
          {t('info-cards.more.label')}
        </Button>
        <Paragraph size="xs">
          {isUserGenerated ? (
            <Trans i18nKey="info-cards.user-generated-info.description">
              <Link to="https://www.familysearch.org/help/helpcenter/article/correct-a-brief-life-history" />
            </Trans>
          ) : (
            <Trans i18nKey="info-cards.computer-generated-info.description">
              <Link to={`/details/${person.id}`} />
              <Link to="https://www.familysearch.org/help/helpcenter/article/correct-a-computer-generated-life-history" />
            </Trans>
          )}
        </Paragraph>
      </InfoCard>
      <Suspense fallback="">
        {(overlay.isOpen || overlay.transitioning) && (
          <LifeHistoryDialog overlay={overlay} text={text} title={heading} />
        )}
      </Suspense>
    </>
  )
}

type AncestorsLifeHistoryCardProps = {
  /** Person whose LifeOfCard is being displayed. */
  person: BasePerson

  /** The LifeOfCard text. */
  text: string
} & Record<string, unknown> // For the infoCardProps

export function AncestorsLifeHistoryCard({
  person,
  text,
  ...infoCardProps
}: AncestorsLifeHistoryCardProps): JSX.Element {
  const [t] = useTranslation()
  const { shortName } = getNameRelatedData(person)
  const overlay = useOverlay()
  const heading = t('info-cards.life-of.header', { shortName })
  return (
    <>
      <InfoCard heading={heading} {...infoCardProps}>
        <LifeHistoryText text={text} />
        <Button data-testid="more-button" onClick={overlay.handleClick}>
          {t('info-cards.more.label')}
        </Button>
      </InfoCard>

      <Suspense fallback="">
        {(overlay.isOpen || overlay.transitioning) && (
          <LifeHistoryDialog overlay={overlay} text={text} title={heading} />
        )}
      </Suspense>
    </>
  )
}

type LifeHistoryTextProps = { text: string }
function LifeHistoryText({ text }: LifeHistoryTextProps): JSX.Element {
  return (
    <div className={lifeOfTextCss}>
      <Paragraph size="sm">
        <Ellipsis noTooltip lineClamp={12}>
          <span className={wrapCss}>{text}</span>
        </Ellipsis>
      </Paragraph>
    </div>
  )
}
